import { Alert, Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import type { ExerciseRun, ExerciseType } from '@/api/exercises'
import { ROUTES } from '@/utils/constants'
import {
  ClozeRenderer,
  DragGapsRenderer,
  DropdownGapsRenderer,
  SentenceBuilderRenderer,
} from './GapRenderers'
import { ChoiceRenderer, PictureRenderer, TrueFalseRenderer } from './ChoiceRenderers'
import { GroupingRenderer, MatchingRenderer, OrderingRenderer } from './OrderRenderers'
import {
  ErrorCorrectionRenderer,
  OpenAnswerRenderer,
  OralAnswerRenderer,
  TranslationRenderer,
} from './TextRenderers'
import { CrosswordRenderer, ListeningRenderer } from './MediaRenderers'
import { DictationRenderer } from './DictationRenderer'
import type {
  ChoiceContent,
  CrosswordContent,
  DictationContent,
  DropdownGapsContent,
  ErrorCorrectionContent,
  GapsContent,
  GroupingContent,
  ListeningContent,
  MatchingContent,
  OpenAnswerContent,
  OralAnswerContent,
  OrderingContent,
  PictureContent,
  SentenceBuilderContent,
  TranslationContent,
  TrueFalseContent,
} from '../contentTypes'

/**
 * Диспетчер рендереров: по типу задания выбирает свой экран решения.
 *
 * Делегатом остался только тест по карточкам — у него своя страница, и
 * повторять её здесь значило бы вести вторую реализацию тех же правил.
 * Диктант решается тут же: отдельной страницы у него больше нет.
 */

type Props = {
  type: ExerciseType
  content: Record<string, unknown>
  answers: Record<string, unknown>
  onChange: (answers: Record<string, unknown>) => void
  readOnly?: boolean
  runId?: number
  /**
   * Сама попытка, если страница её уже загрузила: из неё рендерер берёт
   * серверные поля работы — счётчик прослушиваний и разбор автопроверки.
   * Без неё рендереру пришлось бы вторым запросом тянуть то же самое.
   */
  run?: ExerciseRun | null
  delegateId?: number | null
  /** Статус делегата: его отсутствие означает, что прогона больше нет */
  delegateStatus?: string | null
}

export function ExerciseRenderer({
  type,
  content,
  answers,
  onChange,
  readOnly,
  runId,
  run,
  delegateId,
  delegateStatus,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const shared = { answers, onChange, readOnly }

  switch (type) {
    case 'drag_gaps':
      return <DragGapsRenderer content={content as unknown as GapsContent} {...shared} />
    case 'cloze':
      return <ClozeRenderer content={content as unknown as GapsContent} {...shared} />
    case 'dropdown_gaps':
      return <DropdownGapsRenderer content={content as unknown as DropdownGapsContent} {...shared} />
    case 'choice':
      return <ChoiceRenderer content={content as unknown as ChoiceContent} {...shared} />
    case 'true_false':
      return <TrueFalseRenderer content={content as unknown as TrueFalseContent} {...shared} />
    case 'picture':
      return <PictureRenderer content={content as unknown as PictureContent} {...shared} />
    case 'matching':
      return <MatchingRenderer content={content as unknown as MatchingContent} runId={runId} {...shared} />
    case 'sentence_builder':
      return <SentenceBuilderRenderer content={content as unknown as SentenceBuilderContent} {...shared} />
    case 'ordering':
      return <OrderingRenderer content={content as unknown as OrderingContent} {...shared} />
    case 'grouping':
      return <GroupingRenderer content={content as unknown as GroupingContent} {...shared} />
    case 'error_correction':
      return <ErrorCorrectionRenderer content={content as unknown as ErrorCorrectionContent} {...shared} />
    case 'translation':
      return <TranslationRenderer content={content as unknown as TranslationContent} {...shared} />
    case 'open_answer':
      return <OpenAnswerRenderer content={content as unknown as OpenAnswerContent} {...shared} />
    case 'oral_answer':
      return <OralAnswerRenderer content={content as unknown as OralAnswerContent} runId={runId} {...shared} />
    case 'crossword':
      return <CrosswordRenderer content={content as unknown as CrosswordContent} {...shared} />
    case 'listening':
      return <ListeningRenderer content={content as unknown as ListeningContent} {...shared} />
    case 'dictation':
      return <DictationRenderer content={content as unknown as DictationContent} runId={runId} run={run} {...shared} />
    case 'card_test':
      return (
        <Stack spacing={2}>
          <Typography>{t('exercises.run.delegateCardTest')}</Typography>
          <Stack direction="row">
            <Button
              variant="contained"
              // Делегат мог быть удалён: тогда состояния нет, и вести ученика
              // на несуществующий прогон нельзя
              disabled={!delegateId || !delegateStatus}
              onClick={() => delegateId && navigate(ROUTES.TEST_RUN(delegateId))}
            >
              {t('exercises.run.openCardTest')}
            </Button>
          </Stack>
        </Stack>
      )
    default:
      return <Alert severity="warning">{t('exercises.run.unknownType')}</Alert>
  }
}
